// ─── Personal Insight Engine ─────────────────────────────────────────────────
//
// Generates personalized weekly insights from already-loaded Dashboard data.
// Pure client-side computation — no additional API calls.
//
// Priority scale (determines card ordering):
//   10 — PHQ-4 worsening (clinical flag)
//   9  — PHQ-4 improving
//   8  — Lab deficiency (Vitamin D, B12, CRP elevated, Omega-3, Ferritin)
//   7  — HRV significantly down, borderline lab
//   6  — HRV significantly up
//   5  — Sleep duration drop, Step streak active, Stress spike
//   4  — Exercise-sleep correlation, Stress improvement, Sleep score drop
//   3  — Sleep score improvement, HRV near baseline
//
// Deduplication: one insight per category maximum (except 'lab' — multiple
// lab flags can coexist). Returns up to 6 sorted by priority.
// ─────────────────────────────────────────────────────────────────────────────

import { DailyMetrics, PHQ4Entry } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type InsightCategory =
  | 'hrv'
  | 'sleep'
  | 'movement'
  | 'streak'
  | 'stress'
  | 'mood'
  | 'lab'
  | 'correlation';

export type InsightDirection = 'positive' | 'negative' | 'neutral';

export interface Insight {
  id: string;
  category: InsightCategory;
  priority: number;       // 1–10 — higher = show first
  title: string;          // concise headline (≤6 words)
  detail: string;         // one-sentence supporting context
  direction: InsightDirection;
  value: string;          // e.g. "+14%", "5 days", "21 ng/mL"
  link?: string;          // deep link to relevant page
}

export interface InsightInput {
  metrics: DailyMetrics[];
  phq4Entries?: PHQ4Entry[];
  keyLabs?: Array<{
    marker: string;
    value: number;
    unit: string;
    ref_low: number | null;
    ref_high: number | null;
  }>;
}

// ─── Internal Helpers ─────────────────────────────────────────────────────────

/** Average of non-null values; returns null if fewer than minCount valid points. */
function avg(arr: (number | null | undefined)[], minCount = 3): number | null {
  const vals = arr.filter((v): v is number => v != null && !isNaN(v) && isFinite(v));
  return vals.length >= minCount
    ? vals.reduce((a, b) => a + b, 0) / vals.length
    : null;
}

/** Rounded percentage change from previous to current. */
function pct(current: number, previous: number): number {
  if (previous === 0) return 0;
  return Math.round(((current - previous) / previous) * 100);
}

/** Format a signed percentage string, e.g. "+14%" or "-8%". */
function signed(n: number): string {
  return n > 0 ? `+${n}%` : `${n}%`;
}

// ─── Insight Generator ────────────────────────────────────────────────────────

export function generateInsights(input: InsightInput): Insight[] {
  const { metrics, phq4Entries = [], keyLabs = [] } = input;
  const all: Insight[] = [];

  // Sort metrics ascending by date
  const sorted = [...metrics].sort((a, b) => a.date.localeCompare(b.date));
  const thisWeek = sorted.slice(-7);
  const lastWeek = sorted.slice(-14, -7);

  // ── 1. PHQ-4 Trend ─────────────────────────────────────────────────────────
  if (phq4Entries.length >= 2) {
    const latest = phq4Entries[0];
    const prior  = phq4Entries[1];
    const lScore = latest.score ?? (latest.q1 + latest.q2 + latest.q3 + latest.q4);
    const pScore = prior.score  ?? (prior.q1  + prior.q2  + prior.q3  + prior.q4);
    const delta  = lScore - pScore;

    if (delta <= -2) {
      all.push({
        id: 'phq4-improved', category: 'mood', priority: 9,
        title: 'Mood screen improved',
        detail: `PHQ-4 dropped from ${pScore} to ${lScore} — a meaningful shift in the right direction.`,
        direction: 'positive', value: `${lScore}/12`, link: '/phenotype',
      });
    } else if (delta >= 2) {
      all.push({
        id: 'phq4-worsened', category: 'mood', priority: 10,
        title: 'Mood score elevated',
        detail: `PHQ-4 rose from ${pScore} to ${lScore} over your last two check-ins. Worth monitoring closely.`,
        direction: 'negative', value: `${lScore}/12`, link: '/checkin',
      });
    }
  }

  // ── 2. Lab Deficiencies ────────────────────────────────────────────────────
  const labLookup: Record<string, { value: number; unit: string }> = {};
  for (const lab of keyLabs) {
    const key = lab.marker.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!(key in labLookup)) labLookup[key] = { value: lab.value, unit: lab.unit };
  }

  // Vitamin D
  const vitD = labLookup['vitamind'];
  if (vitD) {
    if (vitD.value < 20) {
      all.push({
        id: 'lab-vitd', category: 'lab', priority: 8,
        title: 'Vitamin D is deficient',
        detail: 'Levels below 20 ng/mL are directly linked to fatigue, low mood, and impaired immune function.',
        direction: 'negative', value: `${vitD.value} ${vitD.unit}`, link: '/labs',
      });
    } else if (vitD.value < 30) {
      all.push({
        id: 'lab-vitd', category: 'lab', priority: 5,
        title: 'Vitamin D is insufficient',
        detail: 'Levels 20–30 ng/mL are below the optimal range for mood and neurological function.',
        direction: 'negative', value: `${vitD.value} ${vitD.unit}`, link: '/labs',
      });
    }
  }

  // B12
  const b12 = labLookup['b12'];
  if (b12 && b12.value < 300) {
    all.push({
      id: 'lab-b12', category: 'lab', priority: 8,
      title: 'B12 may need attention',
      detail: 'Levels below 300 pg/mL can cause cognitive fog, fatigue, and mood dysregulation.',
      direction: 'negative', value: `${b12.value} ${b12.unit}`, link: '/labs',
    });
  }

  // hs-CRP (inflammation)
  const crp = labLookup['hscrp'] ?? labLookup['crp'];
  if (crp) {
    if (crp.value > 3.0) {
      all.push({
        id: 'lab-crp', category: 'lab', priority: 8,
        title: 'Inflammation marker elevated',
        detail: `hs-CRP of ${crp.value} mg/L exceeds the 3.0 threshold linked to immunometabolic depression.`,
        direction: 'negative', value: `${crp.value} mg/L`, link: '/labs',
      });
    } else if (crp.value > 1.0) {
      all.push({
        id: 'lab-crp', category: 'lab', priority: 4,
        title: 'Mild inflammation detected',
        detail: `hs-CRP of ${crp.value} mg/L is in the moderate cardiovascular risk zone. Worth monitoring.`,
        direction: 'negative', value: `${crp.value} mg/L`, link: '/labs',
      });
    }
  }

  // Omega-3 Index
  const omega3 = labLookup['omega3index'];
  if (omega3 && omega3.value < 4) {
    all.push({
      id: 'lab-omega3', category: 'lab', priority: 7,
      title: 'Omega-3 Index is low',
      detail: 'Below 4% EPA+DHA is associated with neuroinflammation and worse mood outcomes.',
      direction: 'negative', value: `${omega3.value}%`, link: '/labs',
    });
  }

  // Ferritin
  const ferritin = labLookup['ferritin'];
  if (ferritin && ferritin.value < 15) {
    all.push({
      id: 'lab-ferritin', category: 'lab', priority: 7,
      title: 'Iron stores are low',
      detail: 'Ferritin below 15 ng/mL is associated with fatigue and reduced brain oxygen delivery.',
      direction: 'negative', value: `${ferritin.value} ${ferritin.unit}`, link: '/labs',
    });
  }

  // ── 3. HRV Week-over-Week Trend ────────────────────────────────────────────
  const thisHRV = avg(thisWeek.map(d => (d.hrv && d.hrv > 0 ? d.hrv : null)));
  const lastHRV = avg(lastWeek.map(d => (d.hrv && d.hrv > 0 ? d.hrv : null)));
  if (thisHRV != null && lastHRV != null) {
    const delta = pct(thisHRV, lastHRV);
    if (Math.abs(delta) >= 8) {
      all.push({
        id: 'hrv-trend', category: 'hrv',
        priority: delta < 0 ? 7 : 6,
        title: delta > 0 ? 'HRV trending up' : 'HRV trending down',
        detail: delta > 0
          ? `7-day avg HRV is ${Math.round(thisHRV)}ms — ${delta}% above last week. Strong autonomic recovery.`
          : `7-day avg HRV is ${Math.round(thisHRV)}ms — ${Math.abs(delta)}% below last week. Prioritize recovery.`,
        direction: delta > 0 ? 'positive' : 'negative',
        value: signed(delta), link: '/sleep',
      });
    }
  }

  // ── 4. Sleep Duration Trend ────────────────────────────────────────────────
  const thisSleep = avg(thisWeek.map(d => d.total_sleep_mins));
  const lastSleep = avg(lastWeek.map(d => d.total_sleep_mins));
  if (thisSleep != null && lastSleep != null) {
    const deltaMins = Math.round(thisSleep - lastSleep);
    if (Math.abs(deltaMins) >= 20) {
      const abs = Math.abs(deltaMins);
      const label = abs >= 60 ? `${Math.floor(abs / 60)}h ${abs % 60}m` : `${abs}m`;
      all.push({
        id: 'sleep-duration', category: 'sleep',
        priority: deltaMins < 0 ? 5 : 4,
        title: deltaMins > 0 ? 'Sleeping more this week' : 'Sleep duration dropped',
        detail: deltaMins > 0
          ? `Averaging ${label} more sleep per night than last week.`
          : `Averaging ${label} less sleep per night than last week.`,
        direction: deltaMins > 0 ? 'positive' : 'negative',
        value: (deltaMins > 0 ? '+' : '') + `${deltaMins}m`, link: '/sleep',
      });
    }
  }

  // ── 5. Step Streak ─────────────────────────────────────────────────────────
  const STEP_TARGET = 7000;
  let streak = 0;
  for (let i = sorted.length - 1; i >= 0; i--) {
    if ((sorted[i].steps ?? 0) >= STEP_TARGET) streak++;
    else break;
  }
  if (streak >= 3) {
    all.push({
      id: 'step-streak', category: 'streak', priority: 5,
      title: `${streak}-day movement streak`,
      detail: `You've hit ${STEP_TARGET.toLocaleString()}+ steps for ${streak} consecutive days — sustained movement supports mood regulation.`,
      direction: 'positive', value: `${streak} days`, link: '/activity',
    });
  }

  // ── 6. Stress Resilience Trend ─────────────────────────────────────────────
  const thisStress = avg(thisWeek.map(d => d.stress_score));
  const lastStress = avg(lastWeek.map(d => d.stress_score));
  if (thisStress != null && lastStress != null) {
    const delta = pct(thisStress, lastStress);
    if (Math.abs(delta) >= 12) {
      all.push({
        id: 'stress-trend', category: 'stress',
        priority: delta < 0 ? 5 : 4,
        title: delta > 0 ? 'Stress resilience up' : 'Physiological stress elevated',
        detail: delta > 0
          ? 'Your body is spending less time in stressed states compared to last week.'
          : 'More time in physiological stress this week — recovery and sleep quality are key.',
        direction: delta > 0 ? 'positive' : 'negative',
        value: signed(delta), link: '/sleep',
      });
    }
  }

  // ── 7. Exercise → Sleep Correlation ───────────────────────────────────────
  const recent14  = sorted.slice(-14);
  const exDays    = recent14.filter(d => (d.exercise_mins ?? 0) >= 20 && d.sleep_score != null);
  const restDays  = recent14.filter(d => (d.exercise_mins ?? 0) < 20 && d.sleep_score != null);
  if (exDays.length >= 3 && restDays.length >= 3) {
    const exAvg   = avg(exDays.map(d => d.sleep_score));
    const restAvg = avg(restDays.map(d => d.sleep_score));
    if (exAvg != null && restAvg != null) {
      const diff = Math.round(exAvg - restAvg);
      if (diff >= 5) {
        all.push({
          id: 'exercise-sleep', category: 'correlation', priority: 4,
          title: 'Exercise boosts your sleep',
          detail: `Your sleep score averages ${diff} points higher on days you exercise 20+ minutes.`,
          direction: 'positive', value: `+${diff} pts`, link: '/activity',
        });
      }
    }
  }

  // ── 8. Sleep → HRV Correlation ────────────────────────────────────────────
  const goodSleep = recent14.filter(d => (d.sleep_score ?? 0) >= 80 && d.hrv != null && d.hrv > 0);
  const poorSleep = recent14.filter(d => (d.sleep_score ?? 0) > 0 && (d.sleep_score ?? 0) < 65 && d.hrv != null && d.hrv > 0);
  if (goodSleep.length >= 3 && poorSleep.length >= 2) {
    const goodHRV = avg(goodSleep.map(d => d.hrv));
    const poorHRV = avg(poorSleep.map(d => d.hrv), 2);
    if (goodHRV != null && poorHRV != null) {
      const diff = Math.round(goodHRV - poorHRV);
      if (diff >= 8) {
        all.push({
          id: 'sleep-hrv', category: 'correlation', priority: 4,
          title: 'Better sleep lifts your HRV',
          detail: `Your HRV averages ${diff}ms higher on nights you score 80+ in sleep.`,
          direction: 'positive', value: `+${diff}ms`, link: '/sleep',
        });
      }
    }
  }

  // ── 9. Steps → Stress Correlation ──────────────────────────────────────────
  const activeDays = recent14.filter(d => (d.steps ?? 0) >= 8000 && d.stress_score != null && d.stress_score > 0);
  const sedentaryDays = recent14.filter(d => (d.steps ?? 0) > 0 && (d.steps ?? 0) < 5000 && d.stress_score != null && d.stress_score > 0);
  if (activeDays.length >= 3 && sedentaryDays.length >= 2) {
    const activeStress = avg(activeDays.map(d => d.stress_score));
    const sedStress = avg(sedentaryDays.map(d => d.stress_score), 2);
    if (activeStress != null && sedStress != null) {
      const diff = Math.round(activeStress - sedStress);
      if (diff >= 10) {
        all.push({
          id: 'steps-stress', category: 'correlation', priority: 4,
          title: 'Movement eases your stress',
          detail: `Your stress resilience is ${diff} points higher on days with 8k+ steps.`,
          direction: 'positive', value: `+${diff} pts`, link: '/activity',
        });
      }
    }
  }

  // ── 10. Sleep Score Trend (fallback if no duration insight) ────────────────
  if (!all.some(i => i.id === 'sleep-duration')) {
    const thisScore = avg(thisWeek.map(d => d.sleep_score));
    const lastScore = avg(lastWeek.map(d => d.sleep_score));
    if (thisScore != null && lastScore != null) {
      const delta = pct(thisScore, lastScore);
      if (Math.abs(delta) >= 8) {
        all.push({
          id: 'sleep-score', category: 'sleep',
          priority: delta < 0 ? 4 : 3,
          title: delta > 0 ? 'Sleep quality improving' : 'Sleep quality dipped',
          detail: `Avg sleep score is ${Math.round(thisScore)}/100 this week (${signed(delta)} vs last week).`,
          direction: delta > 0 ? 'positive' : 'negative',
          value: signed(delta), link: '/sleep',
        });
      }
    }
  }

  // ── Deduplicate + Sort ─────────────────────────────────────────────────────
  // Sort by priority descending. Keep 1 per category except 'lab' (multiple allowed).
  const byPriority = all.sort((a, b) => b.priority - a.priority);
  const seen = new Set<string>();
  const deduped: Insight[] = [];

  for (const ins of byPriority) {
    const key = (ins.category === 'lab' || ins.category === 'correlation') ? ins.id : ins.category;
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(ins);
    }
  }

  return deduped.slice(0, 6);
}
